import { useEffect } from 'react';

/**
 * Paywall modal shown when user exceeds budget limit.
 *
 * Variant 1 (Pro): "Hết giới hạn tuần/tháng" → chờ reset hoặc dùng credit
 * Variant 2 (Free/Trial): "Hết giới hạn dùng thử" → nâng cấp hội viên
 */
export default function PaywallModal({ open, onClose, tier, deniedBy, budgetStatus }) {
    useEffect(() => {
        if (!open) return;
        const handler = (e) => e.key === 'Escape' && onClose?.();
        document.addEventListener('keydown', handler);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handler);
            document.body.style.overflow = '';
        };
    }, [open, onClose]);

    if (!open) return null;

    const isTrial = deniedBy?.window === 'trial' || deniedBy?.limit_type === 'expired';
    const isFree = tier === 'free';
    const windowLabel = deniedBy?.window === 'weekly' ? 'tuần'
        : deniedBy?.window === 'monthly' ? 'tháng'
        : deniedBy?.window === 'trial' ? 'dùng thử'
        : 'sử dụng';

    const resetTime = budgetStatus?.windows?.find(w => w.type === deniedBy?.window)?.reset_time;
    const cashRemaining = budgetStatus?.cash_remaining;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50" onClick={onClose} />
            <div className="relative max-w-sm w-full mx-4 bg-white rounded-2xl shadow-xl p-6">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="text-center space-y-4">
                    <div className="w-12 h-12 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900">
                        Hết giới hạn {windowLabel}
                    </h3>

                    {isFree || isTrial ? (
                        <>
                            <p className="text-sm text-gray-600">
                                Nâng cấp hội viên để tiếp tục sử dụng Nebula AI không giới hạn.
                            </p>
                            <div className="space-y-2 pt-2">
                                <a
                                    href="/pricing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full px-4 py-2.5 bg-brand-blue-500 text-white rounded-xl text-sm font-medium hover:bg-brand-blue-600 transition-colors"
                                >
                                    Mua gói hội viên
                                </a>
                                <button
                                    onClick={onClose}
                                    className="block w-full px-4 py-2 text-gray-600 rounded-xl text-sm hover:bg-gray-100 transition-colors"
                                >
                                    Để sau
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-gray-600">
                                {resetTime
                                    ? `Giới hạn sẽ được làm mới lúc ${resetTime}`
                                    : 'Vui lòng chờ reset hoặc dùng credit để tiếp tục.'
                                }
                            </p>
                            <div className="space-y-2 pt-2">
                                {cashRemaining > 0 && (
                                    <button
                                        className="block w-full px-4 py-2.5 bg-brand-blue-500 text-white rounded-xl text-sm font-medium hover:bg-brand-blue-600 transition-colors"
                                    >
                                        Dùng credit ({cashRemaining} cash)
                                    </button>
                                )}
                                <button
                                    onClick={onClose}
                                    className="block w-full px-4 py-2 text-gray-600 rounded-xl text-sm hover:bg-gray-100 transition-colors"
                                >
                                    {resetTime ? `Chờ reset (${resetTime})` : 'Đóng'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
